import { ReactNode, Component, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import { GaugeHelperHighlight, HighlightJsonSchemeValidator, validateHighlights, getValueFromHighlights, validateHighlightsWithMsg } from "./GaugesHelper";
import { RamStorage } from '@Utils/RamStorage';
// =======================================================================================================
export interface GaugeLabelData {
    GaugeLabelSettings: GaugeLabelSettings;
}
// -------------------------------------------------------------
export interface GaugeLabelSettings {
    MinValue: number;
    MaxValue: number;
    BackgroundColor: string;
    TextColor: string;
    MeasurementUnit: MeasurementUnit;
    CustomMeasurementUnit: string;
    enableDecimal: boolean;
    highlights: GaugeHelperHighlight[];
    decimalPlaces: number;
}
// -------------------------------------------------------------
export interface GaugeLabelState {
}
// -------------------------------------------------------------
export interface GaugeLabelDataPort {
    GaugeLabelValue: number;
    CurrentBackgroundColor: string;
    CurrentTextColor: string;
}
// -------------------------------------------------------------
export enum MeasurementUnit {
    Celcius = 0,
    Fahrenheit = 1,
    Kelvin = 2,
    Percent = 3,
    Custom = 4,
    EmptyUnit = 5
}
// ------------------------------------------------------------------------------------------------------
class GaugeLabel extends Component<Partial<WidgetsThingsStructs.PartialIWidgetThingsProps<GaugeLabelData>>, GaugeLabelState> {
    // ---------------------------------------------------------------------------------------------------
    private StateStorage: RamStorage;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<GaugeLabelData>) {
        super(props);

        // get saved gauge values
        this.StateStorage = RamStorage.GetInstance();
        let savedData = this.StateStorage.GetState(this.props.ThingKey);
        if (savedData == null) {
            savedData = {
                GaugeLabelValue: this.props.WidgetData.GaugeLabelSettings.MinValue,
                CurrentBackgroundColor: this.props.WidgetData.GaugeLabelSettings.BackgroundColor,
                CurrentTextColor: this.props.WidgetData.GaugeLabelSettings.TextColor
            }
        }

        this.state = {
            GaugeLabelValue: savedData.GaugeLabelValue,
            CurrentBackgroundColor: savedData.CurrentBackgroundColor,
            CurrentTextColor: savedData.CurrentTextColor
        };
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const state = this.getDataFromPort();

        // compute number of chars of the value
        let sumChars: number = 0;
        if (isDefined(state.GaugeLabelValue))
            sumChars = state.GaugeLabelValue.toString().length;
        if (this.props.WidgetData.GaugeLabelSettings.MeasurementUnit == MeasurementUnit.Percent || this.props.WidgetData.GaugeLabelSettings.MeasurementUnit == MeasurementUnit.Kelvin)
            sumChars += 0.7;
        else if (this.props.WidgetData.GaugeLabelSettings.MeasurementUnit == MeasurementUnit.Celcius || this.props.WidgetData.GaugeLabelSettings.MeasurementUnit == MeasurementUnit.Fahrenheit)
            sumChars += 1.2;
        else if (this.props.WidgetData.GaugeLabelSettings.MeasurementUnit == MeasurementUnit.Custom &&
            isDefined(this.props.WidgetData.GaugeLabelSettings.CustomMeasurementUnit))
            sumChars += this.props.WidgetData.GaugeLabelSettings.CustomMeasurementUnit.toString().length / 1.5;

        // compute text font size
        if (sumChars < 2)
            sumChars = 2;
        let fontSize = Math.min(this.props.width / sumChars, 0.9 * this.props.height);

        return (
            <div
                style={{
                    width: this.props.width,
                    height: this.props.height,
                    backgroundColor: state.CurrentBackgroundColor,
                    fontSize: fontSize,
                    color: state.CurrentTextColor,
                    lineHeight: this.props.height + "px",
                    fontFamily: "'Share Tech', sans-serif",
                    display: "block",
                    textAlign: "center",
                }}>
                {state.GaugeLabelValue}
                {this.getMeasurementUnit(fontSize)}
            </div >);
    }
    // ---------------------------------------------------------------------------------------------------
    getHighlights() {
        let highlights = this.props.WidgetData.GaugeLabelSettings.highlights;
        if (_.has(this.props.PortsStates, PortKeys.Highlight)) {
            let port = this.props.PortsStates[PortKeys.Highlight];
            if (port.State != null) {
                // if data is not valid; show proper message in render
                if (port.LastStateStatus != StateStatus.Valid) {

                    // logic validation
                    let logicValidation = validateHighlights(this.props.WidgetData.GaugeLabelSettings.MinValue,
                        this.props.WidgetData.GaugeLabelSettings.MaxValue,
                        port.State);

                    if (logicValidation)
                        highlights = port.State;
                }
            }
        }
        return highlights;
    }
    // ---------------------------------------------------------------------------------------------------
    getWidgetColors(newValue: number): { BackgroundColor: string, TextColor: string } {
        // Get gauge label background color.
        let backgroundColor = this.props.WidgetData.GaugeLabelSettings.BackgroundColor;
        // Get gauge label text color.
        let textColor = this.props.WidgetData.GaugeLabelSettings.TextColor;

        // use function from GaugesHelper
        var res = getValueFromHighlights(newValue, this.getHighlights());
        if (res.success === true) {
            backgroundColor = res.color;
            textColor = res.textColor;
        }

        return { BackgroundColor: backgroundColor, TextColor: textColor };
    }
    // ---------------------------------------------------------------------------------------------------
    getMeasurementUnit(fontSize: number) {
        // return the selected measurement unit
        switch (this.props.WidgetData.GaugeLabelSettings.MeasurementUnit) {
            case MeasurementUnit.Celcius:
                return (<sup style={{ fontSize: fontSize / 1.5 }}>
                    <sup style={{ fontSize: fontSize / 2 }}>o</sup>
                    C</sup>);
            case MeasurementUnit.Kelvin:
                return (<sup style={{ fontSize: fontSize / 1.5 }}>
                    K</sup>);
            case MeasurementUnit.Fahrenheit:
                return (<sup style={{ fontSize: fontSize / 1.5 }}>
                    <sup style={{ fontSize: fontSize / 2 }}>o</sup>
                    F</sup>);
            case MeasurementUnit.Percent:
                return (<sup style={{ fontSize: fontSize / 1.5 }}>
                    %</sup>);
            case MeasurementUnit.Custom:
                return (<sup style={{ fontSize: fontSize / 1.5 }}>
                    {this.props.WidgetData.GaugeLabelSettings.CustomMeasurementUnit}</sup>);
            case MeasurementUnit.EmptyUnit:
                return null;
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private getDataFromPort() {
        let state: GaugeLabelDataPort = {
            GaugeLabelValue: this.props.WidgetData.GaugeLabelSettings.MinValue,
            CurrentBackgroundColor: this.props.WidgetData.GaugeLabelSettings.BackgroundColor,
            CurrentTextColor: this.props.WidgetData.GaugeLabelSettings.TextColor
        };

        if (_.has(this.props.PortsStates, PortKeys.Input)) {
            let port = this.props.PortsStates[PortKeys.Input];
            if (port.State != null) {
                if (port.LastStateStatus == StateStatus.Valid) {
                    let data: number = parseFloat(port.State);

                    // check if port value is within the limits
                    if (data >= this.props.WidgetData.GaugeLabelSettings.MinValue &&
                        data <= this.props.WidgetData.GaugeLabelSettings.MaxValue) {
                        let newColors = this.getWidgetColors(data);

                        // Keep the defined number of decimal digits.
                        if ((data % 1) < 1)
                            data = parseFloat(data.toFixed(this.props.WidgetData.GaugeLabelSettings.decimalPlaces));

                        state.GaugeLabelValue = data;
                        state.CurrentBackgroundColor = newColors.BackgroundColor;
                        state.CurrentTextColor = newColors.TextColor;
                    }
                }
            }
        }

        return state;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
interface GaugeLabelWidgetSettingsState {
    widgetData: GaugeLabelData;
}

class GaugeLabelWidgetSettings extends Component<IWidgetSettingsProps<GaugeLabelData>, GaugeLabelWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<GaugeLabelData>) {
        super(props);
        this.state = { widgetData: _.cloneDeep(this.props.WidgetData) };
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.widgetData);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave={true}
            Data={this.state.widgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [{
                        type: SettingsType.Number,
                        label: 'Min Value',
                        dataPath: 'GaugeLabelSettings.MinValue',
                        oneLine: true,
                        tooltip: 'Minimum value of gauge',
                        isDecimal: true,
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Max Value',
                        dataPath: 'GaugeLabelSettings.MaxValue',
                        oneLine: true,
                        tooltip: 'Maximum value of gauge',
                        isDecimal: true,
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Decimal Places',
                        dataPath: 'GaugeLabelSettings.decimalPlaces',
                        minValue: 0,
                        maxValue: 20,
                        oneLine: true,
                        tooltip: 'The number of digits after the decimal point'
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        dataPath: 'GaugeLabelSettings.BackgroundColor',
                        tooltip: 'Color used in the background of the widget'
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Text Color',
                        dataPath: 'GaugeLabelSettings.TextColor',
                        tooltip: 'Set the color of the displayed information'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Decimal Highlights',
                        dataPath: 'GaugeLabelSettings.enableDecimal',
                        tooltip: 'Whether to enable decimal values in from and to values of highlights.'
                    },
                    {
                        type: SettingsType.Highlights,
                        label: 'Highlights',
                        dataPath: 'GaugeLabelSettings.highlights',
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        tooltip: 'Define different colors for value ranges',
                        enableDecimal: 'GaugeLabelSettings.enableDecimal',
                        showTextColor: true,
                        minValue: 'GaugeLabelSettings.MinValue',
                        maxValue: 'GaugeLabelSettings.MaxValue'
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Measurement Unit',
                        dataPath: 'GaugeLabelSettings.MeasurementUnit',
                        tooltip: 'Select unit of the value from the supported ones (`Celsius`, `Fahrenheit`, `Kelvin`, `Percent`, `Empty Unit`).',
                        items: [
                            { label: 'Celcius', value: MeasurementUnit.Celcius },
                            { label: 'Fahrenheit', value: MeasurementUnit.Fahrenheit },
                            { label: 'Kelvin', value: MeasurementUnit.Kelvin },
                            { label: 'Percent', value: MeasurementUnit.Percent },
                            { label: 'Empty Unit', value: MeasurementUnit.EmptyUnit },
                            { label: 'Custom', value: MeasurementUnit.Custom }
                        ]
                    },
                    {
                        type: SettingsType.String,
                        label: 'Custom Measurement',
                        dataPath: 'GaugeLabelSettings.CustomMeasurementUnit',
                        tooltip: 'Fill custom measurement unit',
                        isHidden: (data: GaugeLabelData) =>
                            data.GaugeLabelSettings.MeasurementUnit != MeasurementUnit.Custom
                    }]
                }]}
            onCheckData={(data) => {
                const settings = data.GaugeLabelSettings;
                // todo: use Micro.ts function (if we don't have this kind of helper function, export to Micro.ts)
                const isNumber = (v) => {
                    return !isNaN(parseFloat(v)) && isFinite(v);
                };

                let isValid: boolean = true;
                let errorMsg: string = '';

                if (!isNumber(settings.MinValue)) {
                    errorMsg = 'Min Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MaxValue)) {
                    errorMsg = 'Max Value should be numeric';
                    isValid = false;
                }
                else if (settings.MinValue > settings.MaxValue) {
                    errorMsg = 'Max Value should be greater than Min Value';
                    isValid = false;
                }
                else {
                    const highlightsRes = validateHighlightsWithMsg(settings.MinValue, settings.MaxValue, settings.highlights);
                    if (!highlightsRes.valid)
                        return highlightsRes;

                }

                return {
                    valid: isValid,
                    errorMsg: errorMsg
                };
            }}
            onSaveData={(data) => {
                this.setState(
                    { widgetData: data },
                    () => this.saveData());
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface
const enum PortKeys {
    Input = "input",
    Highlight = "highlight"
}
export class GaugeLabelWidget extends IWidgetThingsClass<GaugeLabelData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <GaugeLabel />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <GaugeLabelWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        this.GetPortsFromSettings().forEach((p) => {
            thing.AddPort(p, false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    private GetPortsFromSettings(): WebThingPort[] {
        const ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: "Input",
                PortKey: PortKeys.Input,
                Type: Plegma.ePortType.Decimal,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data));

        ports.push(new WebThingPort(
            {
                Name: "Highlight",
                PortKey: PortKeys.Highlight,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: "",
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Highlight, data)));

        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this.GetPortsFromSettings();

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Highlight)
            return HighlightJsonSchemeValidator().DataValidate(value);
        return { valid: true, errors: '' };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): GaugeLabelData {
        return {
            GaugeLabelSettings: {
                MinValue: 0,
                MaxValue: 100,
                TextColor: "rgba(102, 204, 102, 1)",
                BackgroundColor: "rgba(255, 255, 255, 1)",
                MeasurementUnit: MeasurementUnit.Celcius,
                CustomMeasurementUnit: "",
                highlights: [],
                decimalPlaces: 2,
                enableDecimal: false
            }
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return GaugeLabelWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: "Gauge Label",
            WidgetTitle: "",
            Key: "GaugeLabelWidget",
            Type: "GaugeLabelWidget",
            Data: GaugeLabelWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/gauge_label.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Input; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================